
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { usePartners } from '@/services/use-partners'
import { Partner } from '@/services/partner-service'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'
import { MediaPicker } from '@/components/media/media-picker'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { ImageIcon } from 'lucide-react'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { getMediaUrl } from '@/utils/url-utils'

const partnerSchema = z.object({
  name: z.string().min(1, 'Referans/Marka adı zorunludur'),
  imageUrl: z.string().optional(),
  orderIndex: z.number().default(0),
  isActive: z.boolean().default(true),
})

type PartnerFormValues = z.infer<typeof partnerSchema>

export const Route = createFileRoute('/_authenticated/referanslar/')({
  component: PartnersPage,
})

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: false },
  { title: 'Referans Yönetimi', href: '/referanslar', isActive: true },
]

function PartnersPage() {
  const { partners, isLoading, isCreating, isUpdating, isDeleting, createPartner, updatePartner, deletePartner } = usePartners()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null)

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [partnerToDelete, setPartnerToDelete] = useState<string | null>(null)

  const form = useForm<PartnerFormValues>({
    resolver: zodResolver(partnerSchema),
    defaultValues: {
      name: '',
      imageUrl: '',
      orderIndex: 0,
      isActive: true,
    },
  })

  // Hacky way to handle opening dialog with reset state
  const openNewDialog = () => {
    form.reset({ name: '', imageUrl: '', orderIndex: partners.length, isActive: true })
    setEditingPartner(null)
    setIsDialogOpen(true)
  }

  const openEditDialog = (partner: Partner) => {
    form.reset({
      name: partner.name,
      imageUrl: partner.imageUrl || '',
      orderIndex: partner.orderIndex,
      isActive: partner.isActive,
    })
    setEditingPartner(partner)
    setIsDialogOpen(true)
  }

  const onSubmit = async (values: PartnerFormValues) => {
    try {
      if (editingPartner) {
        await updatePartner({ id: editingPartner._id, data: values })
      } else {
        await createPartner(values)
      }
      setIsDialogOpen(false)
    } catch (error) {
      console.error(error)
    }
  }

  const handleDeleteClick = (id: string) => {
    setPartnerToDelete(id)
    setIsDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (partnerToDelete) {
      try {
        await deletePartner(partnerToDelete)
        setIsDeleteDialogOpen(false)
        setPartnerToDelete(null)
      } catch (error) {
        console.error(error)
      }
    }
  }

  if (isLoading) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex h-[80vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Main>
      </>
    )
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Referanslar & Partnerler</h1>
            <p className="text-muted-foreground text-sm">Alt bantta kayan firmaları ve marka logolarını yönetin.</p>
          </div>
          <Button onClick={openNewDialog}>
            <Plus className="mr-2 h-4 w-4" /> Yeni Referans Ekle
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Marka Listesi</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>
            ) : partners.length === 0 ? (
              <div className="text-center p-8 text-muted-foreground">Henüz referans eklenmemiş.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Sıra</TableHead>
                    <TableHead>Firma Adı</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead className="text-right">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partners.map((partner) => (
                    <TableRow key={partner._id}>
                      <TableCell className="font-medium">
                        <Badge variant="outline">{partner.orderIndex}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 border rounded">
                            <AvatarImage src={getMediaUrl(partner.imageUrl)} className="object-contain p-1" />
                            <AvatarFallback className="text-[10px]">LOGO</AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{partner.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={partner.isActive ? 'default' : 'secondary'}>
                          {partner.isActive ? 'Aktif' : 'Pasif'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="outline" size="icon" onClick={() => openEditDialog(partner)}>
                          <Pencil className="h-4 w-4 text-blue-500" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleDeleteClick(partner._id)}
                        >
                          {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 text-red-500" />}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Form Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingPartner ? 'Referans Düzenle' : 'Yeni Referans'}</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Firma Adı</FormLabel>
                      <FormControl>
                        <Input placeholder="Örn: Aselsan, TUSAŞ vs." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="orderIndex"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sıra No</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="imageUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Logo</FormLabel>
                        <div className="flex gap-2">
                          <FormControl>
                            <Input placeholder="Seçilmedi" {...field} readOnly className="bg-muted" />
                          </FormControl>
                          <Button type="button" variant="secondary" onClick={() => setIsPickerOpen(true)}>
                            <ImageIcon className="h-4 w-4 mr-2" /> Seç
                          </Button>
                        </div>
                        {field.value && (
                          <div className="mt-2 h-20 w-32 border rounded flex items-center justify-center p-2 bg-muted/30">
                            <img src={getMediaUrl(field.value)} alt="Preview" className="max-h-full max-w-full object-contain" />
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="isActive"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Görünürlük</FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    İptal
                  </Button>
                  <Button type="submit" disabled={isCreating || isUpdating}>
                    {isCreating || isUpdating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {editingPartner ? 'Güncelle' : 'Kaydet'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        <MediaPicker
          open={isPickerOpen}
          onOpenChange={setIsPickerOpen}
          onSelect={(url) => {
            form.setValue('imageUrl', url, { shouldDirty: true })
            setIsPickerOpen(false)
          }}
          title="Firma Logosu Seç"
        />

        <ConfirmDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          title="Referansı Sil"
          desc="Bu referansı silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
          destructive
          isLoading={isDeleting}
          handleConfirm={handleConfirmDelete}
        />
      </Main>
    </>
  )
}